/**
 * Complete the Look — bundle builder
 *
 * Two custom elements working together, no full re-renders:
 *  - <complete-the-look-item>  one product card (variant picker + include toggle)
 *  - <complete-the-look>       the section shell (sum, installments, thumbs, buy)
 *
 * Reuses the theme's money helpers (formatPrice + theme.settings.money_with_currency_format)
 * and mirrors the installment rule from snippets/price.liquid so the total always
 * matches the product page.
 */
(() => {
  const moneyFromCents = cents => {
    const format = window.theme?.settings?.money_with_currency_format;
    if (typeof formatPrice === 'function' && format) {
      return formatPrice(format, cents / 100);
    }
    return (cents / 100).toFixed(2);
  };

  /* --------------------------------------------------------------------- */
  /*  Item                                                                 */
  /* --------------------------------------------------------------------- */
  if (!customElements.get('complete-the-look-item')) {
    class CompleteTheLookItem extends HTMLElement {
      connectedCallback() {
        if (this._initialised) return;
        this._initialised = true;

        this.variants = this.readVariants();
        this.baseOptions = this.readBaseOptions();
        this.selector = this.querySelector('.product-selector');
        this.checkbox = this.querySelector('[data-include]');
        this.image = this.querySelector('[data-media-image]');
        this.hint = this.querySelector('[data-hint]');
        this.needsSelection = false;

        this.computeStock();

        const config = this.closest('complete-the-look')?.config;

        // Optional: start with no size chosen (merchant setting on the section).
        if (config?.preselectSize === false) {
          this.clearSelection();
        }

        if (this.selector) {
          this.selector.addEventListener('change', this.onVariantChange.bind(this));
        }
        this.querySelector('form')?.addEventListener('submit', event => event.preventDefault());
        if (this.checkbox) {
          this.checkbox.addEventListener('change', () => this.emitChange());
        }
        this.addEventListener('click', this.onCardClick.bind(this));

        this.resolveCurrentVariant();
        this.updateVariantStatuses();
        this.syncActiveStates();
        this.reflectState();
      }

      /**
       * Widens the include/exclude hit area.
       *  - Desktop: only the product image (the badge toggles natively).
       *  - Mobile: anywhere that isn't the image, the picker or the title.
       * The title link and the size picker always win, so they are checked first.
       */
      onCardClick(event) {
        if (!this.checkbox || this.checkbox.disabled) return;

        if (
          event.target.closest('[data-title-link]') ||
          event.target.closest('.complete-the-look-card__variants') ||
          event.target.closest('.complete-the-look-card__toggle')
        ) {
          return;
        }

        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        const onImage = !!event.target.closest('[data-media-link]');

        if (isDesktop) {
          if (!onImage) return;
          event.preventDefault(); // the image toggles instead of navigating
        } else if (onImage) {
          return; // on mobile the image keeps linking to the product
        }

        this.checkbox.checked = !this.checkbox.checked;
        this.emitChange();
      }

      syncActiveStates() {
        this.querySelectorAll('.product-selector input[type="radio"]').forEach(input => {
          input.closest('.product-options__item')?.classList.toggle('is-active', input.checked);
        });
      }

      readVariants() {
        const node = this.querySelector('[data-variants]');
        if (!node) return [];
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return [];
        }
      }

      readBaseOptions() {
        try {
          return JSON.parse(this.dataset.selectedOptions || '[]');
        } catch (error) {
          return [];
        }
      }

      /** Positions (1-based) of the options actually rendered as radios. */
      get renderedPositions() {
        return Array.from(
          this.querySelectorAll('.product-selector fieldset[data-option-position]'),
          fieldset => Number(fieldset.dataset.optionPosition)
        );
      }

      /**
       * Full option tuple for the current selection: starts from the product's
       * selected variant and overrides only the positions the card exposes.
       */
      get optionValues() {
        const values = this.baseOptions.slice();
        this.querySelectorAll('.product-selector input[type="radio"]:checked').forEach(input => {
          const position = Number(input.dataset.optionPosition);
          if (position) values[position - 1] = input.value;
        });
        return values;
      }

      matchVariant(options) {
        return this.variants.find(variant => variant.options.every((option, i) => options[i] === option));
      }

      /** Stock is judged only across the options the customer can actually change. */
      computeStock() {
        const rendered = this.renderedPositions;
        this.hasStock = this.variants.some(
          variant =>
            variant.available &&
            variant.options.every((option, i) => rendered.includes(i + 1) || option === this.baseOptions[i])
        );
      }

      clearSelection() {
        this.needsSelection = true;
        this.querySelectorAll('.product-selector input[type="radio"]').forEach(input => {
          input.checked = false;
          input.nextElementSibling?.classList.remove('is-active');
        });
      }

      resolveCurrentVariant() {
        if (this.needsSelection) {
          this.currentVariant = null;
          return;
        }
        this.currentVariant = this.matchVariant(this.optionValues) || this.variants.find(v => v.available) || this.variants[0];
      }

      onVariantChange() {
        this.needsSelection = false;
        this.syncActiveStates();
        this.resolveCurrentVariant();
        this.updateVariantStatuses();
        this.updateImage();
        this.hideHint();
        this.emitChange();
      }

      /* Disable option values that would resolve to a missing or sold-out variant. */
      updateVariantStatuses() {
        const current = this.optionValues;

        this.querySelectorAll('.product-selector fieldset[data-option-position]').forEach(fieldset => {
          const position = Number(fieldset.dataset.optionPosition);

          fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
            const candidate = current.slice();
            candidate[position - 1] = input.value;
            const variant = this.matchVariant(candidate);
            const usable = !!variant && variant.available;

            input.disabled = !usable;
            input.closest('.product-options__item')?.classList.toggle('is-disabled', !usable);
          });
        });
      }

      updateImage() {
        const src = this.currentVariant?.featured_image?.src;
        if (src && this.image) {
          this.image.src = src;
          this.image.srcset = '';
        }
      }

      emitChange() {
        this.reflectState();
        this.dispatchEvent(new CustomEvent('look:change', { bubbles: true }));
      }

      reflectState() {
        const included = this.isIncluded();
        this.classList.toggle('is-included', included);
        this.classList.toggle('is-unavailable', !this.hasStock);
        if (this.checkbox) {
          this.checkbox.disabled = !this.hasStock;
          if (!this.hasStock) this.checkbox.checked = false;
        }
      }

      isIncluded() {
        if (!this.hasStock) return false;
        if (this.checkbox && !this.checkbox.checked) return false;
        return !!this.currentVariant && this.currentVariant.available && !this.needsSelection;
      }

      showHint(message) {
        if (!this.hint) return;
        this.hint.textContent = message;
        this.hint.classList.remove('hidden');
      }

      hideHint() {
        this.hint?.classList.add('hidden');
      }

      getState() {
        return {
          included: this.isIncluded(),
          needsSelection: this.needsSelection && (this.checkbox ? this.checkbox.checked : true),
          available: this.hasStock,
          variantId: this.currentVariant?.id || null,
          price: this.currentVariant?.price || 0,
          image: this.image?.src || '',
          productId: this.dataset.productId
        };
      }
    }

    customElements.define('complete-the-look-item', CompleteTheLookItem);
  }

  /* --------------------------------------------------------------------- */
  /*  Section shell                                                        */
  /* --------------------------------------------------------------------- */
  if (!customElements.get('complete-the-look')) {
    class CompleteTheLook extends HTMLElement {
      connectedCallback() {
        this.config = this.readConfig();
        this.itemsContainer = this.querySelector('[data-items]');
        this.totalEl = this.querySelector('[data-total]');
        this.installmentsEl = this.querySelector('[data-installments]');
        this.thumbsEl = this.querySelector('[data-thumbnails]');
        this.buyButton = this.querySelector('[data-buy]');
        this.feedbackEl = this.querySelector('[data-feedback]');

        this.addEventListener('look:change', () => this.recompute());
        this.addEventListener('click', this.onAccordionClick.bind(this));
        this.buyButton?.addEventListener('click', this.buy.bind(this));

        this.recompute();
      }

      readConfig() {
        const node = this.querySelector('[data-config]');
        const fallback = {
          maxInstallments: 6,
          minInstallment: 5000,
          preselectSize: true,
          strings: {}
        };
        if (!node) return fallback;
        try {
          return { ...fallback, ...JSON.parse(node.textContent) };
        } catch (error) {
          return fallback;
        }
      }

      get items() {
        return Array.from(this.querySelectorAll('complete-the-look-item'));
      }

      /* Inline accordion blocks. */
      onAccordionClick(event) {
        const button = event.target.closest('[data-accordion-button]');
        if (!button) return;
        const accordion = button.closest('[data-accordion]');
        const content = accordion?.querySelector('[data-accordion-content]');
        if (!content) return;
        const isOpen = accordion.classList.toggle('is-open');
        content.classList.toggle('is-open', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
      }

      recompute() {
        const states = this.items.map(item => item.getState());
        const selected = states.filter(state => state.included);
        const totalCents = selected.reduce((sum, state) => sum + state.price, 0);

        if (this.totalEl) this.totalEl.textContent = moneyFromCents(totalCents);
        if (this.installmentsEl) {
          this.installmentsEl.textContent = totalCents > 0 ? this.installmentText(totalCents) : '';
        }

        this.updateThumbnails(states);

        if (this.buyButton) {
          this.buyButton.disabled = selected.length === 0;
          const label = this.buyButton.querySelector('[data-buy-label]');
          if (label && this.config.strings.buyWithCount) {
            label.textContent = selected.length
              ? this.config.strings.buyWithCount.replace('[count]', selected.length)
              : this.config.strings.buy;
          }
        }
      }

      updateThumbnails(states) {
        if (!this.thumbsEl) return;
        const byId = new Map(states.map(state => [state.productId, state]));
        this.thumbsEl.querySelectorAll('[data-thumb]').forEach(thumb => {
          const state = byId.get(thumb.dataset.thumb);
          thumb.classList.toggle('hidden', !state || !state.included);
          const img = thumb.querySelector('img');
          if (img && state?.image) img.src = state.image;
        });
      }

      installmentText(cents) {
        const { maxInstallments, minInstallment, strings } = this.config;
        let count = Math.floor(cents / minInstallment);
        if (count > maxInstallments) count = maxInstallments;

        if (count >= 2) {
          // Round up, never down: truncating would advertise an installment
          // that adds up to less than the total actually charged.
          const per = Math.ceil(cents / count);
          return [
            strings.installmentPrefix,
            `${count}x`,
            strings.installmentJoin,
            moneyFromCents(per),
            strings.installmentSuffix
          ]
            .filter(Boolean)
            .join(' ');
        }
        return strings.cash || '';
      }

      buy() {
        const cartItems = [];
        let skipped = 0;

        this.items.forEach(item => {
          const state = item.getState();
          if (state.needsSelection) {
            skipped += 1;
            item.showHint(this.config.strings.selectSize);
            return;
          }
          if (state.included && state.variantId) {
            cartItems.push({ id: state.variantId, quantity: 1 });
          }
        });

        if (!cartItems.length) {
          this.showFeedback(this.config.strings.selectSize, true);
          return;
        }

        this.setLoading(true);

        const cartDrawer = document.querySelector('cart-drawer');
        const body = { items: cartItems };
        if (cartDrawer) {
          body.sections = cartDrawer.getSectionsToRender().map(section => section.section);
          body.sections_url = window.location.pathname;
        }

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        })
          .then(response => response.json())
          .then(response => {
            // /cart/add.js reports failures with a `status` (422) + `description`.
            if (response.status && response.status !== 200) {
              this.showFeedback(response.description || this.config.strings.error, true);
              return;
            }
            if (cartDrawer) {
              cartDrawer.renderContents(response);
            } else {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }
            if (skipped) this.showFeedback(this.config.strings.selectSize, true);
          })
          .catch(() => this.showFeedback(this.config.strings.error, true))
          .finally(() => this.setLoading(false));
      }

      setLoading(state) {
        this.buyButton?.classList.toggle('disabled', state);
        if (this.buyButton) this.buyButton.disabled = state;
      }

      showFeedback(message, isError) {
        if (!this.feedbackEl || !message) return;
        this.feedbackEl.textContent = message;
        this.feedbackEl.classList.toggle('is-error', !!isError);
        this.feedbackEl.classList.remove('hidden');
        clearTimeout(this._feedbackTimer);
        this._feedbackTimer = setTimeout(() => {
          this.feedbackEl.classList.add('hidden');
        }, 5000);
      }
    }

    customElements.define('complete-the-look', CompleteTheLook);
  }
})();
